import { Text } from '@thanx/uikit/text'
import React from 'react'
import { StyleObject, useStyletron } from 'styletron-react'
import { RECOVERY_AI_CONTENT_MAX_WIDTH } from './layout'

export type SettingsPaneChromeProps = {
  /** Pane heading. Omit for a title-less pane (ActiveLocations renders none). */
  title?: string
  /** Grey supporting line under the heading. */
  subtitle?: string
  /**
   * Constrain to the Settings content column. EscalationContacts passes false —
   * it ships full-bleed with no maxWidth today.
   */
  constrainWidth?: boolean
  /** Gap between the header block and the body: 'xl' (48px) is the config-pane
   *  rhythm; EscalationContacts uses 'l' (32px), pinned to its Figma spacing. */
  contentGap?: 'l' | 'xl'
  children: React.ReactNode
}

// Pure + exported so the width logic is unit-testable: css() emits no classes
// under renderWithStore (no StyletronProvider), so the class name itself is
// unassertable from the DOM.
export const settingsPaneWidthStyle = (constrainWidth: boolean): StyleObject =>
  constrainWidth ? { maxWidth: RECOVERY_AI_CONTENT_MAX_WIDTH } : {}

// The layout header the RecoveryAI Settings panes share: the padded, width-capped
// column plus an optional heading + grey subtitle block. Deliberately NOT a
// uniform pane surface — the panes differ genuinely on save shape, dirty
// semantics, hydration and permission gating, so banners, Save and data-fetching
// all stay with each pane.
//
// Settings-tab only. A modal never renders this: ModalContainer owns the dialog
// title, and that omission IS how the duplicate heading is suppressed — there is
// no hideTitle prop to get wrong.
const SettingsPaneChrome = ({
  title,
  subtitle,
  constrainWidth = true,
  contentGap = 'xl',
  children,
}: SettingsPaneChromeProps) => {
  const [css] = useStyletron()
  const widthClass = constrainWidth
    ? css(settingsPaneWidthStyle(constrainWidth))
    : ''

  return (
    <div className={`p-l ${widthClass}`}>
      {(title || subtitle) && (
        <div className={`d-flex flex-column mb-${contentGap}`}>
          {title && <Text.Header3 bold>{title}</Text.Header3>}
          {/* BodyText3 renders an inline <span>, which ignores vertical margins
              — the gap to the body must live on this block wrapper. */}
          {subtitle && (
            <Text.BodyText3 color="grey70" className="mt-xs">
              {subtitle}
            </Text.BodyText3>
          )}
        </div>
      )}
      {children}
    </div>
  )
}

export default SettingsPaneChrome
